import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiPhone, FiSend, FiMessageSquare } from 'react-icons/fi';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Footer from '../components/Layout/Footer';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', type: 'feedback', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      return toast.error('Please fill in all fields');
    }
    setLoading(true);
    try {
      await api.post('/contact', form);
      toast.success('Message sent! We\'ll get back to you soon.');
      setSubmitted(true);
    } catch {
      toast.error('Failed to send message. Please email us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <nav style={styles.nav}><Link to="/" style={styles.brand}>ReplyCraft</Link></nav>
      <div style={styles.container}>
        <h1 style={styles.title}>Contact Us</h1>
        <p style={styles.subtitle}>Have feedback, a suggestion, or need help? We'd love to hear from you.</p>

        <div style={styles.grid}>
          {/* Contact Info */}
          <div style={styles.infoSection}>
            <div style={styles.infoCard}>
              <FiMail size={24} color="var(--accent)" />
              <h3 style={styles.infoTitle}>Email</h3>
              <a href="mailto:support@replycraft.io" style={styles.infoLink}>support@replycraft.io</a>
              <p style={styles.infoDesc}>We typically respond within 24 hours</p>
            </div>
            <div style={styles.infoCard}>
              <FiPhone size={24} color="var(--accent)" />
              <h3 style={styles.infoTitle}>Phone</h3>
              <a href="sms:+15403227406" style={styles.infoLink}>(540) 322-7406</a>
              <p style={styles.infoDesc}>Text only — no voice calls</p>
            </div>
            <div style={styles.infoCard}>
              <FiMessageSquare size={24} color="var(--accent)" />
              <h3 style={styles.infoTitle}>Feature Requests</h3>
              <p style={styles.infoDesc}>Have an idea for a new tool or feature? Let us know! We review every suggestion and update ReplyCraft regularly.</p>
            </div>
          </div>

          {/* Contact Form */}
          <div style={styles.formSection}>
            {submitted ? (
              <div style={styles.successBox}>
                <div style={styles.successIcon}>&#10003;</div>
                <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Message Sent!</h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>Thank you for reaching out. We'll get back to you soon.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', type: 'feedback', message: '' }); }} style={styles.sendAnotherBtn}>Send Another Message</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Name</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" required style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" required style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Type</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={styles.select}>
                    <option value="feedback">General Feedback</option>
                    <option value="suggestion">Feature Suggestion</option>
                    <option value="bug">Bug Report</option>
                    <option value="support">Account Support</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Message</label>
                  <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Tell us what's on your mind... feature ideas, feedback, issues, or just say hi!" rows={6} required style={styles.textarea} />
                </div>
                <button type="submit" disabled={loading} style={styles.submitBtn}>
                  <FiSend size={16} /> {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>

        <div style={styles.note}>
          <p>We read every message. If you have a tool idea or suggestion, we'll consider it for our next version update!</p>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column' },
  nav: { padding: '16px 40px', borderBottom: '1px solid var(--border)' },
  brand: { fontSize: 20, fontWeight: 700, color: 'var(--accent)', textDecoration: 'none' },
  container: { maxWidth: 900, margin: '0 auto', padding: '40px 20px 60px', flex: 1 },
  title: { fontSize: 36, fontWeight: 800, textAlign: 'center', marginBottom: 8, color: 'var(--text-primary)' },
  subtitle: { textAlign: 'center', color: 'var(--text-secondary)', fontSize: 16, marginBottom: 40 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 },
  infoSection: { display: 'flex', flexDirection: 'column', gap: 16 },
  infoCard: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 14, padding: 24 },
  infoTitle: { fontSize: 16, fontWeight: 700, margin: '10px 0 4px', color: 'var(--text-primary)' },
  infoLink: { color: 'var(--accent)', fontSize: 15, textDecoration: 'none', fontWeight: 500 },
  infoDesc: { fontSize: 13, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 },
  formSection: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, padding: 28 },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  formGroup: {},
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 },
  input: { width: '100%', padding: '12px 14px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, outline: 'none' },
  select: { width: '100%', padding: '12px 14px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, outline: 'none' },
  textarea: { width: '100%', padding: '12px 14px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, outline: 'none', resize: 'vertical', lineHeight: 1.6 },
  submitBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer' },
  successBox: { textAlign: 'center', padding: '40px 20px' },
  successIcon: { width: 56, height: 56, borderRadius: '50%', background: 'rgba(34,197,94,0.15)', color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28, fontWeight: 700 },
  sendAnotherBtn: { padding: '10px 24px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer' },
  note: { marginTop: 40, padding: 20, background: 'var(--accent-dim)', border: '1px solid var(--accent)', borderRadius: 12, textAlign: 'center', color: 'var(--text-primary)', fontSize: 15, lineHeight: 1.6 }
};
